import { useCallback, useEffect, useMemo, useState } from 'react'
import { useOutletContext } from 'react-router-dom';

// Components
import MyAccountContentHeader from '../MyAccountContentHeader/MyAccountContentHeader';

// Utils
import * as translator from '../../../../utils/translator';
import * as http from '../../../../utils/http';
import AddNewAddressCard from './AddNewAddressCard';
import AddressCard from './AddressCard';
import {  CustomerAddressCreation } from './schemas';
import AddressFormSheet from './AddressFormSheet';
import useIsMobile from '../../../../customHooks/useIsMobile';


const MyAccountDeliveryAddressV2 = () => {
  const { setActiveMenuLinkId, setStickyNotificationDetails, setRefreshData, refreshData, language, country, setLoading, setError } = useOutletContext<any>();
  
  const isMobile = useIsMobile();
  const [deliveryAddress, setDeliveryAddress] = useState<any>(null);
  const addresses: { regular: any[], packstation: any[] } = deliveryAddress;
  const supportPackstation: boolean = deliveryAddress?.supportPackstation ?? false;
  const [addressAttributes, setAddressAttributes] = useState<CustomerAddressCreation | null>(null);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [formKey, setFormKey] = useState(0);


  useEffect(() => {
    setActiveMenuLinkId("delivery-address");

    setLoading(true);

		http
			.getFromServer(`${country}/account/delivery-address`, [http.MIDDLEWARES.CUSTOMER_ID])
			.then((response) => {
				setDeliveryAddress(response.data);
			})
			.catch((error) => {
				window.clientLogger.error('Error in MyAccountDeliveryAddressV2: ', error);
				setError(500);
			})
			.finally(() => {
				setLoading(false);
			});

  }, [refreshData]);

  const toggleEditSheet = useCallback((addressToEdit?: any) => {
    setAddressAttributes(addressToEdit);
    setIsSheetOpen(true);
    setFormKey(prevKey => prevKey + 1);
  }, []);

  const toggleCreateSheet = useCallback(() => {
    setAddressAttributes(null);
    setIsSheetOpen(true);
    setFormKey(prevKey => prevKey + 1);
  }, []);

  const isNewAddress = useMemo(() => !addressAttributes, [addressAttributes, isSheetOpen]);

  const onSubmit = (formData: CustomerAddressCreation): void => {
    http
      .postToServer(`account-update/delivery-address`,
        { 
          ...formData, 
          id: addressAttributes?.id,
          country: formData.country_id,
        },
        [http.MIDDLEWARES.CUSTOMER_ID]
      )
      .then((response) => {
        setRefreshData((current: boolean) => !current)
        setStickyNotificationDetails((currentDetails: any) => ({
          show: true,
          title: "",
          content: translator.translate(language, "my_account", `stickyNotifications/success/deliveryAddress`),
          level: "info",
          showInfoIcon: false,
          refresh: !currentDetails.refresh
        }))
      })
      .catch((error) => {
        const content = error?.response?.status === 429 ? translator.translate(language, "my_account", `stickyNotifications/error/deliveryAddress`) ?? "Please wait 10 seconds before making another address change" : "error occurred, please try again later";
        setStickyNotificationDetails((currentDetails: any) => ({
          show: true,
          title: content,
          content: '',
          level: error?.response?.status === 429 ? "warning" : "error",
          showInfoIcon: false,
          refresh: !currentDetails.refresh
        }))
      })
      .finally(() => {
        toggleCloseSheet()
      })

  };

  const toggleCloseSheet = useCallback(() => {
    setIsSheetOpen(false);
    setTimeout(() => {
      setAddressAttributes(null);
    }, 100);
  }, []);

  const handleDelete = (id: number) => {
    http.postToServer(`account-update/delivery-address/delete`,
      { address_id: id },
      [http.MIDDLEWARES.CUSTOMER_ID]
    )
      .then((response) => {
        setRefreshData((current: boolean) => !current)
        setStickyNotificationDetails((currentDetails: any) => ({
          show: true,
          title: "",
          content: translator.translate(language, "my_account", `DeliveryAddressV2/addressDeleteSuccess`),
          level: "info",
          showInfoIcon: false,
          refresh: !currentDetails.refresh
        }))
      })
      .catch((error) => {
        setStickyNotificationDetails((currentDetails:any)=>({
          show: true,
          title: "Oops",
          content: translator.translate(language, "my_account", `DeliveryAddressV2/addressDeleteError`),
          level : "error",
          showInfoIcon:false,
          refresh : !currentDetails.refresh
        }))
      })
  };

  const handleSetDefault = (id: number) => {
    http.postToServer(`account-update/delivery-address/set-default`,
      { address_id: id },
      [http.MIDDLEWARES.CUSTOMER_ID]
    )
      .then((response) => {
        setRefreshData((current: boolean) => !current)
        setStickyNotificationDetails((currentDetails: any) => ({
          show: true,
          title: "",
          content: translator.translate(language, "my_account", `DeliveryAddressV2/addressSetDefaultSuccess`),
          level: "info",
          showInfoIcon: false,
          refresh: !currentDetails.refresh
        }))
      })
      .catch((error) => {
        setStickyNotificationDetails((currentDetails: any) => ({
          show: true,
          title: "Oops",
          content: translator.translate(language, "my_account", `DeliveryAddressV2/addressSetDefaultError`),
          level: "error",
          showInfoIcon: false,
          refresh: !currentDetails.refresh
        }))
      })
  };


  const hasOnlyOneAddress = [deliveryAddress?.regular, deliveryAddress?.packstation].filter(Boolean).length === 1;

  // If no address data is available, render nothing
  if (!addresses) {
    return null;
  }
  
  return (
    <>
      <div id="delivery-address-v2-container" className="flex flex-col px-4 gap-y-4 w-full">
        <MyAccountContentHeader
          title={translator.translate(language, "my_account", `DeliveryAddressV2/title`)}
          description={translator.translate(language, "my_account", `DeliveryAddressV2/subTitle`)}
        />

        <h2 id='personal-address-title' className='cursor-default text-2xl sm:text-3xl font-semibold text-[#0D2C54]'>{translator.translate(language, "my_account", `DeliveryAddressV2/address`)}</h2>
        <div id='personal-container' className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <AddNewAddressCard
            language={language}
            onClick={() => toggleCreateSheet()}
          />
          {addresses && addresses.regular?.map((address: any, idx: number) => (
            <AddressCard
              headerTitle={`${translator.translate(language, "my_account", `DeliveryAddressV2/address`)} ${idx + 1}`}
              key={address.id}
              id={address.id}
              first_name={address.first_name}
              last_name={address.last_name}
              address={address.address || ''}
              zip={address.zip || ''}
              city={address.city || ''}
              country_id={address.country_id || address.country || ''}
              phone={address.phone || ''}
              isDefault={address.is_default}
              isOnlyAddress={hasOnlyOneAddress}
              handleEdit={() => toggleEditSheet(address)}
              handleDelete={() => handleDelete(address.id)}
              handleSetDefault={() => handleSetDefault(address.id)}
              language={language}
            />
          ))}
        </div>
        {supportPackstation && (
          <>
            <h2 id='packstation-address-title' className='mt-4 cursor-default text-2xl sm:text-3xl font-semibold text-[#0D2C54]'>{translator.translate(language, "my_account", `DeliveryAddressV2/packstation`)}</h2>
            <div id='packstation-container' className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {addresses && addresses.packstation?.map((packstationAddress: any, idx: number) => {
                const displayAddress = `Packstation ${packstationAddress.packstation_number || ''}, ${packstationAddress.packstation_address || ''}`;
                return (
                  <AddressCard
                    key={packstationAddress.id}
                    id={packstationAddress.id}
                    headerTitle={`${translator.translate(language, "my_account", `DeliveryAddressV2/packstation`)} ${idx + 1}`}
                    first_name={packstationAddress.first_name}
                    last_name={packstationAddress.last_name}
                    address={displayAddress}
                    cardType='packstation'
                    zip={packstationAddress.zip || ''}
                    city={packstationAddress.city || ''}
                    country_id={packstationAddress.country_id}
                    phone={packstationAddress.phone || ''}
                    isDefault={packstationAddress.is_default}
                    isOnlyAddress={hasOnlyOneAddress}
                    handleEdit={() => toggleEditSheet(packstationAddress)}
                    handleDelete={() => handleDelete(packstationAddress.id)}
                    handleSetDefault={() => handleSetDefault(packstationAddress.id)}
                    language={language}
                  />
                );
              })}
            </div>
          </>
        )}
      </div>
  

      <AddressFormSheet
        key={formKey}
        isOpen={isSheetOpen}
        onClose={toggleCloseSheet}
        onSubmit={onSubmit}
        language={language}
        country={country}
        addressAttributes={addressAttributes as CustomerAddressCreation}
        isNewAddress={isNewAddress}
        isMobile={isMobile}
        supportPackstation={supportPackstation}
      />
    </>
  )
}

export default MyAccountDeliveryAddressV2